import {
  attendanceHistoryPageSchema,
  attendanceKioskLinkSchema,
  attendanceLedgerEntrySchema,
  attendanceLedgerPageSchema,
  attendancePageSchema,
  attendanceRecordSchema,
  attendanceSettingsSchema,
  leaveRequestPageSchema,
  leaveRequestSchema,
  type AttendanceHistoryPage,
  type AttendanceKioskLink,
  type AttendanceLedgerEntry,
  type AttendanceLedgerKind,
  type AttendanceLedgerPage,
  type AttendancePage,
  type AttendanceRecord,
  type AttendanceSettings,
  type AttendanceStatus,
  type CheckInInput,
  type CheckOutInput,
  type CreateAttendanceLedgerEntryInput,
  type CreateLeaveRequestInput,
  type DecideLeaveRequestInput,
  type LeaveRequest,
  type LeaveRequestPage,
  type LeaveRequestStatus,
  type RecordAttendanceInput,
  type UpdateAttendanceSettingsInput,
  type UserRole,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every attendance call the app makes.
 *
 * Note what is NOT sent from here. `workedMinutes`, `late`, `halfDay` and `source` appear in no
 * request below, because the server computes all four — a browser that could set them could also
 * decide it had never been late. The same rule runs the other way on the way back: the hours cell
 * renders the minutes the server returned, and nothing in this module subtracts one timestamp
 * from another.
 *
 * Arriving and leaving have their own endpoints rather than being a `record` with a status,
 * because they are the two things somebody presses a button for; `recordAttendance` is the
 * manager's correction — and the one that marks a day as leave — and is the only one of the
 * three that names the day it is writing.
 */

const ATTENDANCE = '/attendance';

export interface AttendanceSearchParams {
  /** One shop-timezone `YYYY-MM-DD`. Omitted lets the server decide today. */
  day?: string;
  /** Repeated query parameter, as `QuotationSearchParams.status` is. */
  role?: readonly UserRole[];
  status?: AttendanceStatus;
  q?: string;
  page?: number;
  pageSize?: number;
}

/** Who is in the shop on one day. What the attendance screen reads. */
export async function searchAttendance(
  client: ApiClient,
  params: AttendanceSearchParams = {},
): Promise<AttendancePage> {
  return await client.get(ATTENDANCE, {
    schema: attendancePageSchema,
    query: {
      ...(params.day ? { day: params.day } : {}),
      ...(params.role?.length ? { role: [...params.role] } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.q ? { q: params.q } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

export interface AttendanceHistoryParams {
  staffUserId: string;
  /** Inclusive shop-timezone `YYYY-MM-DD`. */
  from: string;
  to: string;
  page?: number;
  pageSize?: number;
}

/**
 * One person over a date range, with the range's own totals.
 *
 * The day search cannot answer this — it is scoped to a single date — and the totals come down
 * with it rather than being added up from `items`, which would report a page as a fortnight.
 */
export async function getAttendanceHistory(
  client: ApiClient,
  params: AttendanceHistoryParams,
): Promise<AttendanceHistoryPage> {
  return await client.get(`${ATTENDANCE}/history`, {
    schema: attendanceHistoryPageSchema,
    query: {
      staffUserId: params.staffUserId,
      from: params.from,
      to: params.to,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 31,
    },
  });
}

export async function getAttendanceSettings(client: ApiClient): Promise<AttendanceSettings> {
  return await client.get(`${ATTENDANCE}/settings`, { schema: attendanceSettingsSchema });
}

/** Only the keys that changed — the contract refuses an empty patch. */
export async function updateAttendanceSettings(
  client: ApiClient,
  patch: UpdateAttendanceSettingsInput,
): Promise<AttendanceSettings> {
  return await client.patch(`${ATTENDANCE}/settings`, patch, { schema: attendanceSettingsSchema });
}

/** Arriving. The day is the server's, not the browser's clock's. */
export async function checkIn(client: ApiClient, input: CheckInInput): Promise<AttendanceRecord> {
  return await client.post(`${ATTENDANCE}/check-in`, input, { schema: attendanceRecordSchema });
}

/** Leaving. Same shape, same reasoning. */
export async function checkOut(client: ApiClient, input: CheckOutInput): Promise<AttendanceRecord> {
  return await client.post(`${ATTENDANCE}/check-out`, input, { schema: attendanceRecordSchema });
}

/** Writing or correcting one named day — the upsert on `(staffUserId, day)`. */
export async function recordAttendance(
  client: ApiClient,
  input: RecordAttendanceInput,
): Promise<AttendanceRecord> {
  return await client.post(`${ATTENDANCE}/record`, input, { schema: attendanceRecordSchema });
}

// --- The attendance kiosk link (task 18) -------------------------------------------------------

const KIOSK_LINK = `${ATTENDANCE}/kiosk-link`;

/** What the settings screen reads: the link as it stands, or all-null if there is none. */
export async function getAttendanceKioskLink(client: ApiClient): Promise<AttendanceKioskLink> {
  return await client.get(KIOSK_LINK, { schema: attendanceKioskLinkSchema });
}

/** Creates the link on first use, or replaces it and clears any revocation. */
export async function rotateAttendanceKioskLink(client: ApiClient): Promise<AttendanceKioskLink> {
  return await client.post(`${KIOSK_LINK}/rotate`, undefined, {
    schema: attendanceKioskLinkSchema,
  });
}

/** Switches the link off. The token stops answering from the very next request. */
export async function revokeAttendanceKioskLink(client: ApiClient): Promise<void> {
  await client.del(KIOSK_LINK);
}

// --- The HR ledger and leave requests (tier 2, item 9) ------------------------------------------

/**
 * Overtime and permission.
 *
 * Note what is not here either: no money. An entry carries minutes and a reason, and what
 * overtime is WORTH is resolved by the payroll run against the rate card — which is also what
 * lets a karigar read their own ledger without that being a karigar reading money.
 */

const LEDGER = `${ATTENDANCE}/ledger`;

export interface LedgerParams {
  /** Inclusive shop-timezone `YYYY-MM-DD`. Both required — the ledger is always a range. */
  from: string;
  to: string;
  staffUserId?: string;
  kind?: AttendanceLedgerKind;
  page?: number;
  pageSize?: number;
}

export async function getAttendanceLedger(
  client: ApiClient,
  params: LedgerParams,
): Promise<AttendanceLedgerPage> {
  return await client.get(LEDGER, {
    schema: attendanceLedgerPageSchema,
    query: {
      from: params.from,
      to: params.to,
      ...(params.staffUserId ? { staffUserId: params.staffUserId } : {}),
      ...(params.kind ? { kind: params.kind } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
    },
  });
}

export async function createLedgerEntry(
  client: ApiClient,
  input: CreateAttendanceLedgerEntryInput,
): Promise<AttendanceLedgerEntry> {
  return await client.post(LEDGER, input, { schema: attendanceLedgerEntrySchema });
}

export async function deleteLedgerEntry(client: ApiClient, entryId: string): Promise<void> {
  await client.del(`${LEDGER}/${entryId}`);
}

const LEAVE = `${ATTENDANCE}/leave-requests`;

export interface LeaveRequestParams {
  status?: LeaveRequestStatus;
  pendingOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export async function getLeaveRequests(
  client: ApiClient,
  params: LeaveRequestParams = {},
): Promise<LeaveRequestPage> {
  return await client.get(LEAVE, {
    schema: leaveRequestPageSchema,
    query: {
      ...(params.status ? { status: params.status } : {}),
      ...(params.pendingOnly ? { pendingOnly: 'true' } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
    },
  });
}

export async function askForLeave(
  client: ApiClient,
  input: CreateLeaveRequestInput,
): Promise<LeaveRequest> {
  return await client.post(LEAVE, input, { schema: leaveRequestSchema });
}

/**
 * The decision, and what it did to the roster.
 *
 * The response is deliberately more than the request itself: approving marks every day in the
 * range `on_leave`, EXCEPT any day somebody had already worked, and the screen has to be able to
 * say which — "three days marked, Thursday left as it was" is the sentence that stops an owner
 * wondering whether it worked.
 */
export const leaveDecisionSchema = z.object({
  request: leaveRequestSchema,
  daysMarked: z.array(z.string()),
  daysSkipped: z.array(z.string()),
});
export type LeaveDecision = z.infer<typeof leaveDecisionSchema>;

export async function decideLeaveRequest(
  client: ApiClient,
  requestId: string,
  input: DecideLeaveRequestInput,
): Promise<LeaveDecision> {
  return await client.post(`${LEAVE}/${requestId}/decision`, input, {
    schema: leaveDecisionSchema,
  });
}

export async function cancelLeaveRequest(
  client: ApiClient,
  requestId: string,
): Promise<LeaveRequest> {
  return await client.post(`${LEAVE}/${requestId}/cancel`, {}, { schema: leaveRequestSchema });
}
